/**
 * ArticleDetailPage — canonical reader for /insights/:slug
 * Fetches content_items by slug. Clean editorial layout.
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLAR_META = {
  'healthcare':       { label: 'Healthcare',       color: '#1d6f6a', bg: 'rgba(29,111,106,.1)',  link: '/coverage/healthcare'       },
  'government':       { label: 'Government',        color: '#16304a', bg: 'rgba(22,48,74,.1)',    link: '/coverage/government'       },
  'business-finance': { label: 'Business & Finance',color: '#b45309', bg: 'rgba(180,83,9,.1)',    link: '/coverage/business-finance' },
  'education':        { label: 'Education',          color: '#5E3F8F', bg: 'rgba(94,63,143,.1)',  link: '/coverage/education'        },
  'technology-ai':    { label: 'Technology & AI',   color: '#1B6B72', bg: 'rgba(27,107,114,.1)',  link: '/coverage/technology-ai'    },
  'workforce-labor':  { label: 'Workforce & Labor', color: '#b45309', bg: 'rgba(180,83,9,.1)',    link: '/coverage/workforce-labor'  },
};

// ── States ────────────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="article-loading">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto mb-4"
          style={{ borderColor: 'var(--op-teal)' }} />
        <p style={{ color: 'var(--op-muted)', fontSize: '0.875rem' }}>Loading article…</p>
      </div>
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="article-not-found">
      <div className="text-center" style={{ maxWidth: 420, padding: '64px 24px' }}>
        <p style={{ fontSize: '4rem', fontWeight: 800, color: 'var(--op-navy)', margin: '0 0 8px' }}>404</p>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--op-navy)', marginBottom: 8 }}>
          Article Not Found
        </h1>
        <p style={{ color: 'var(--op-muted)', fontSize: '0.9rem', marginBottom: 24 }}>
          This article doesn't exist or may have been removed.
        </p>
        <Link to="/" style={{ color: 'var(--op-teal)', fontWeight: 600, fontSize: '0.875rem' }}
          data-testid="article-notfound-home">
          ← Return to Homepage
        </Link>
      </div>
    </div>
  );
}

function UnavailableState() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="article-unavailable">
      <div className="text-center" style={{ maxWidth: 420, padding: '64px 24px' }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--op-navy)', marginBottom: 8 }}>
          Not Yet Published
        </h1>
        <p style={{ color: 'var(--op-muted)', fontSize: '0.9rem', marginBottom: 24 }}>
          This article is not available yet.
        </p>
        <Link to="/" style={{ color: 'var(--op-teal)', fontWeight: 600, fontSize: '0.875rem' }}>
          ← Return to Homepage
        </Link>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | found | notfound | unavailable

  useEffect(() => {
    setStatus('loading');
    axios.get(`${API}/content/slug/${slug}`)
      .then(r => {
        const item = r.data;
        if (!item) { setStatus('notfound'); return; }
        if (item.status !== 'published') { setStatus('unavailable'); return; }
        setArticle(item);
        setStatus('found');
      })
      .catch(err => {
        setStatus(err?.response?.status === 404 ? 'notfound' : 'notfound');
      });
  }, [slug]);

  if (status === 'loading')     return <LoadingState />;
  if (status === 'notfound')    return <NotFoundState />;
  if (status === 'unavailable') return <UnavailableState />;

  return <ArticleReader article={article} />;
}

// ── Reader layout ─────────────────────────────────────────────────────────────

function ArticleReader({ article }) {
  const pillar  = PILLAR_META[article.primary_pillar] || null;
  const cleanBody = article.body
    ? DOMPurify.sanitize(article.body, { USE_PROFILES: { html: true } })
    : '';

  const pubDate = article.published_at
    ? format(parseISO(article.published_at), 'MMMM d, yyyy')
    : null;

  return (
    <div
      style={{
        background: 'var(--op-paper, #faf8f4)',
        minHeight: '100vh',
        fontFamily: "'Public Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        WebkitFontSmoothing: 'antialiased',
      }}
      data-testid="article-detail-page"
    >
      <div className="container-custom" style={{ paddingTop: 40, paddingBottom: 80 }}>

        {/* ── Breadcrumb ── */}
        <nav style={{ marginBottom: 24 }} data-testid="article-breadcrumb">
          <Link
            to="/"
            style={{ fontSize: '0.8125rem', color: 'var(--op-muted)', textDecoration: 'none' }}
          >
            Home
          </Link>
          {pillar && (
            <>
              <span style={{ color: 'var(--op-muted)', margin: '0 6px', fontSize: '0.75rem' }}>›</span>
              <Link
                to={pillar.link}
                style={{ fontSize: '0.8125rem', color: 'var(--op-muted)', textDecoration: 'none' }}
              >
                {pillar.label}
              </Link>
            </>
          )}
          <span style={{ color: 'var(--op-muted)', margin: '0 6px', fontSize: '0.75rem' }}>›</span>
          <span style={{ fontSize: '0.8125rem', color: 'var(--op-navy)' }}>Article</span>
        </nav>

        {/* ── Readable column ── */}
        <div style={{ maxWidth: 760, margin: '0 auto' }}>

          {/* Sector badge */}
          {pillar && (
            <Link
              to={pillar.link}
              style={{
                display: 'inline-block', marginBottom: 16,
                fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', textDecoration: 'none',
                color: pillar.color, background: pillar.bg,
                padding: '4px 12px', borderRadius: 4,
              }}
              data-testid="article-sector-badge"
            >
              {pillar.label}
            </Link>
          )}

          {/* Title */}
          <h1
            style={{
              fontFamily: "'Newsreader', Georgia, serif",
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 800, lineHeight: 1.2,
              color: 'var(--op-navy, #081D30)',
              margin: '0 0 16px', letterSpacing: '-0.02em',
            }}
            data-testid="article-title"
          >
            {article.title}
          </h1>

          {/* Dek */}
          {article.dek && (
            <p
              style={{
                fontSize: '1.125rem', lineHeight: 1.55,
                color: '#3a4249', margin: '0 0 24px',
                fontStyle: 'italic',
              }}
              data-testid="article-dek"
            >
              {article.dek}
            </p>
          )}

          {/* Meta row */}
          <div
            style={{
              display: 'flex', flexWrap: 'wrap', gap: '6px 16px',
              alignItems: 'center', marginBottom: 28,
              paddingBottom: 20, borderBottom: '1px solid var(--op-border, #e8e4db)',
            }}
            data-testid="article-meta"
          >
            {article.byline && (
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--op-navy)' }}>
                {article.byline}
              </span>
            )}
            {pubDate && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--op-muted)' }}>{pubDate}</span>
            )}
            {article.estimated_read_minutes > 0 && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--op-muted)' }}>
                · {article.estimated_read_minutes} min read
              </span>
            )}
          </div>

          {/* Hero image */}
          {article.featured_image_url && (
            <figure style={{ margin: '0 0 32px' }} data-testid="article-hero-image">
              <img
                src={article.featured_image_url}
                alt={article.image_caption || article.title}
                style={{
                  width: '100%', borderRadius: 8, display: 'block',
                  aspectRatio: '16 / 9', objectFit: 'cover',
                }}
              />
              {(article.image_caption || article.image_credit) && (
                <figcaption style={{
                  fontSize: '0.75rem', color: 'var(--op-muted)',
                  marginTop: 8, lineHeight: 1.4,
                }}>
                  {article.image_caption}
                  {article.image_caption && article.image_credit && ' — '}
                  {article.image_credit}
                </figcaption>
              )}
            </figure>
          )}

          {/* Summary callout (shown only when body is long) */}
          {article.summary && article.word_count > 300 && (
            <blockquote
              style={{
                margin: '0 0 28px',
                padding: '14px 20px',
                borderLeft: `3px solid ${pillar?.color || 'var(--op-teal)'}`,
                background: 'rgba(255,255,255,.7)',
                borderRadius: '0 6px 6px 0',
                fontStyle: 'italic',
                color: '#3a4249', fontSize: '1rem', lineHeight: 1.6,
              }}
              data-testid="article-summary"
            >
              {article.summary}
            </blockquote>
          )}

          {/* Body */}
          {cleanBody && (
            <div
              className="op-article-body"
              dangerouslySetInnerHTML={{ __html: cleanBody }}
              data-testid="article-body"
            />
          )}


          {/* Back link */}
          <div style={{ marginTop: 48, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {pillar && (
              <Link
                to={pillar.link}
                style={{
                  fontSize: '0.875rem', fontWeight: 600,
                  color: pillar.color, textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
                data-testid="article-back-sector"
              >
                ← More in {pillar.label}
              </Link>
            )}
            <Link
              to="/"
              style={{
                fontSize: '0.875rem', color: 'var(--op-muted)',
                textDecoration: 'none',
              }}
              data-testid="article-back-home"
            >
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
